import { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PageTitle } from "../../../components/Headers";
import { PageBody } from "../../../components/PageBody";
import { ArticleParagraph } from "../../../components/Paragraphs";
import { getSEOForPath } from "../../../config/seo";
import { ClueEditor } from "../../../components/games/nonogramSolver/ClueEditor";
import { NonogramRow } from "../../../components/games/nonogramSolver/NonogramRow";
import { StageMinSpan } from "../../../components/games/nonogramSolver/StageMinSpan";
import { StageOverlap } from "../../../components/games/nonogramSolver/StageOverlap";
import { StageCombinations } from "../../../components/games/nonogramSolver/StageCombinations";
import { solveRow } from "../../../components/games/nonogramSolver/nonogramSolver";
import { createEmptyCellStates, cycleCellState, resizeCellStates } from "../../../components/games/nonogramSolver/cellState";

const DEFAULT_ROW_LENGTH = 10;
const DEFAULT_CLUES = [3, 4];

export const NonogramSolver = () => {
    const location = useLocation();
    const seo = getSEOForPath(location.pathname);

    const [rowLength, setRowLength] = useState(DEFAULT_ROW_LENGTH);
    const [clues, setClues] = useState(DEFAULT_CLUES);
    const [cellStates, setCellStates] = useState(() => createEmptyCellStates(DEFAULT_ROW_LENGTH));

    const handleRowLengthChange = (newLength) => {
        setRowLength(newLength);
        setCellStates((prev) => resizeCellStates(prev, newLength));
    };

    const handleCellClick = (index) => {
        setCellStates((prev) => {
            const next = [...prev];
            next[index] = cycleCellState(next[index]);
            return next;
        });
    };

    const handleResetMarks = () => setCellStates(createEmptyCellStates(rowLength));

    const result = useMemo(() => solveRow(rowLength, clues, cellStates), [rowLength, clues, cellStates]);

    return (
        <PageBody>
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                {seo.keywords && <meta name="keywords" content={seo.keywords} />}
            </Helmet>

            <PageTitle title="Nonogram Row Solver" />

            <ArticleParagraph
                lines={[
                    "A nonogram clue like \"3 4\" tells you the length of each block of filled cells in a row, in order — but not exactly where they sit. Some cells, though, are forced: no matter how the blocks slide around, they have to be filled or empty.",
                    "Set a row length and clue below, then mark any cells you already know as filled or crossed off. Watch how the guaranteed cells — and the reasoning behind them — change as you add constraints."
                ]}
            />

            <ClueEditor
                rowLength={rowLength}
                onRowLengthChange={handleRowLengthChange}
                clues={clues}
                onCluesChange={setClues}
                error={result.isImpossible ? `This clue needs at least ${result.minSpan} cells.` : null}
            />

            <NonogramRow
                cellStates={cellStates}
                guaranteedCells={result.isImpossible ? null : result.guaranteedCells}
                onCellClick={handleCellClick}
                contradiction={result.contradiction}
            />

            <div className="flex justify-center mb-6">
                <button
                    type="button"
                    onClick={handleResetMarks}
                    className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors min-h-[44px]"
                >
                    Reset marks
                </button>
            </div>

            <hr className="mb-4" />

            <StageMinSpan
                rowLength={rowLength}
                clues={clues}
                minSpan={result.minSpan}
                slack={result.slack}
                isImpossible={result.isImpossible}
                forcedOverlapSegments={result.forcedOverlapSegments}
            />

            {!result.isImpossible && (
                <>
                    <StageOverlap
                        leftmost={result.leftmost}
                        rightmost={result.rightmost}
                        overlapCells={result.overlapCells}
                    />

                    <StageCombinations
                        placements={result.placements}
                        placementsTruncated={result.placementsTruncated}
                        totalValidCount={result.totalValidCount}
                        guaranteedCells={result.guaranteedCells}
                        contradiction={result.contradiction}
                        enumerationOverflow={result.enumerationOverflow}
                    />
                </>
            )}
        </PageBody>
    );
};
